/**
 * @render react
 * @name Collapsible
 * @example
 * <Collapsible title="Equipment" content="Content of the collapsible" iconOpen="open" iconClose="close" isOpen />
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqid from 'uniqid';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Heading } from 'Components/molecules/Heading';

import './style.scss';

/**
 * Collapsible component
 * @param {boolean} isOpen - flag to show the collapsible open or closed on load
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {func} onToggle - Callback for toggling the collapsible
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {string} content - Content to show within the collapsible
 * @param {string} iconClose - close icon to show (default -)
 * @param {string} iconOpen - open icon to show (default +)
 * @param {string} title - Title to show on the header
 * @param {string} key - The key of the component
 */

const Collapsible = (
  {
    key,
    title,
    titleComponent,
    content,
    iconOpen,
    iconClose,
    isOpen,
    onToggle,
    loadStyles,
    extraClassNames,
    context: { cssPrefix, platform },
  },
) => {
  // Status for our collapsible. For default it's closed
  const [isCollapsibleOpen, setCollapsibleOpen] = useState(isOpen);
  const unique = uniqid();

  /**
   * Toggles the collapsible. Open/close
   */
  const toggleCollapsible = keyValue => (
    onToggle ? onToggle(keyValue) : setCollapsibleOpen(!isCollapsibleOpen)
  );

  /**
   * Check if the props are changing to dynamically open/close
   */
  useEffect(() => {
    if (onToggle) {
      setCollapsibleOpen(isOpen);
    }
  });

  /**
   * Get icon to be used.
   */
  const getIcon = () => {
    if (isCollapsibleOpen) {
      return iconClose ? <Icon icon={iconClose} /> : <span>&#45;</span>;
    }
    return iconOpen ? <Icon icon={iconOpen} /> : <span>&#43;</span>;
  };

  // Render
  return (
    <div
      className={classNames({
        [`${cssPrefix}__collapsible`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {platform === 'amp' && (
        <amp-state id={`isOpen${unique}`}>
          <script type="application/json">
            {isOpen ? 'true' : 'false'}
          </script>
        </amp-state>
      )}

      <button
        className={`${cssPrefix}__collapsible--header`}
        onClick={() => toggleCollapsible(key)}
        role="tab"
        type="button"
        aria-expanded={isCollapsibleOpen}
        on={platform === 'amp' ? `tap:AMP.setState({ isOpen${unique}: !isOpen${unique} })` : undefined}
        data-amp-bind-aria-expanded={platform === 'amp' && !onToggle ? `(isOpen${unique} ? 'true' : 'false')` : undefined} // eslint-disable-line
      >
        {titleComponent && (
          <div className={`${cssPrefix}__collapsible--title-component`}>{titleComponent}</div>
        )}

        {title && (
          <Heading size={5} extraClassNames={{ [`${cssPrefix}__collapsible--title`]: true }}>{title}</Heading>
        )}

        <span className={`${cssPrefix}__collapsible--icon`}>
          {getIcon()}
        </span>
      </button>

      {content && (
        <div
          className={`${cssPrefix}__collapsible--content`}
          aria-hidden={!isCollapsibleOpen}
          data-amp-bind-aria-hidden={platform === 'amp' && !onToggle ? `(isOpen${unique} ? 'false' : 'true')` : undefined} // eslint-disable-line
          role="tabpanel"
        >
          {content}
        </div>
      )}
    </div>
  );
};

/**
 * Valid Props
 */
Collapsible.propTypes = {
  key: PropTypes.string,
  titleComponent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  title: PropTypes.string,
  content: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  iconOpen: PropTypes.string,
  iconClose: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  // eslint-disable-next-line react/no-unused-prop-types
  ampScripts: PropTypes.shape(),
};

/**
 * Default Props
 */
Collapsible.defaultProps = {
  key: '',
  titleComponent: null,
  title: '',
  content: '',
  iconOpen: null,
  iconClose: null,
  isOpen: true,
  onToggle: null,
  loadStyles: true,
  extraClassNames: {},
  context: {},
  ampScripts: {
    'amp-bind': 'https://cdn.ampproject.org/v0/amp-bind-0.1.js',
  },
};

export default withPolaris(Collapsible);
